import { useEffect, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import EditablePage from "@/components/pagebuilder/EditablePage";
import api from "@/lib/api";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useT } from "@/context/LanguageContext";
import { localizedContent } from "@/lib/localize";

export default function Contact() {
  const { t, lang } = useT();
  const [page, setPage] = useState(null);
  useEffect(() => {
    api.get("/cms/pages/contact").then((r) => setPage(r.data)).catch(() => {});
  }, []);
  const c = localizedContent(page?.content, lang);
  const locations = c.locations || [];

  return (
    <PublicLayout>
      <EditablePage slug="contact" page={page} onSaved={setPage}>
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28" data-testid="contact-page">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dojo-ink-soft)] mb-4">{c.eyebrow || "Contact"}</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] mb-6">
          {c.heading || "Visit a dojo near you"}
        </h1>
        {c.intro && (
          <p className="text-lg text-[var(--dojo-ink-soft)] font-light max-w-2xl mb-12">{c.intro}</p>
        )}

        {/* Locations */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {locations.map((loc, i) => (
            <div key={i} className="bg-[var(--dojo-paper-alt)] border border-[var(--dojo-border)] p-8" data-testid={`contact-location-${i}`}>
              <div className="font-kanji text-3xl text-[var(--dojo-green)] mb-4">道場</div>
              <h3 className="font-serif text-xl mb-4">{loc.name}</h3>
              {loc.address && (
                <div className="flex gap-3 items-start text-sm text-[var(--dojo-ink-soft)] mb-3">
                  <MapPin size={16} className="text-[var(--dojo-green)] mt-0.5 shrink-0" />
                  <span>{loc.address}</span>
                </div>
              )}
              {loc.phone && (
                <a href={`tel:${loc.phone.replace(/[^0-9+]/g, "")}`} className="flex gap-3 items-center text-sm text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-green)]">
                  <Phone size={16} className="text-[var(--dojo-green)] shrink-0" />
                  <span>{loc.phone}</span>
                </a>
              )}
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
          <div className="space-y-6">
            {c.hours && (
              <div className="flex gap-5 items-start">
                <div className="p-3 border border-[var(--dojo-border)]"><Clock size={16} /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-1">{t("page.hours")}</div>
                  <div className="text-lg font-serif">{c.hours}</div>
                </div>
              </div>
            )}
            {c.email && (
              <div className="flex gap-5 items-start">
                <div className="p-3 border border-[var(--dojo-border)]"><Mail size={16} /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-1">{t("page.email")}</div>
                  <div className="text-lg font-serif">{c.email}</div>
                </div>
              </div>
            )}
          </div>
          <div className="bg-[var(--dojo-paper-alt)] border border-[var(--dojo-border)] p-8 md:p-10">
            <div className="font-kanji text-4xl text-[var(--dojo-green)] mb-4">押忍</div>
            <p className="text-[var(--dojo-ink-soft)] leading-relaxed mb-6">
              New students are welcome to observe a class before enrolling. Please call the location nearest you so we can greet you properly.
            </p>
            <p className="text-sm text-[var(--dojo-ink-soft)] leading-relaxed">
              Once you have received an access code from the dojo, you may complete enrollment through our online portal.
            </p>
          </div>
        </div>
      </section>
      </EditablePage>
    </PublicLayout>
  );
}
